import { useCallback, useEffect, useState } from 'react'
import { API_URL } from '../api/config'

export interface RegistrationRecord {
  id: string;
  registrationUid?: string;
  usuarioNome: string;
  usuarioDocumento: string;
  usuarioEmail: string;
  usuarioTelefone: string;
  loja: string;
  sala: string | null;
  codigoAcesso: string;
  dataInscricaoFormatada: string;
  etiqueta: {
    nome: string;
  } | null;
  temCheckin: boolean;
  totalAcessos: number;
  ultimoAcesso: string | null;
}

export interface RegistrationListResponse {
  data: RegistrationRecord[];
  total: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
  stats?: {
    totalComCheckin: number;
    totalSemCheckin: number;
    porLoja: Array<{ loja: string; total: number }>;
  };
}

export interface RegistrationFilters {
  userName?: string;
  userDocument?: string;
  store?: string;
  startDate?: string;
  endDate?: string;
}

export const useRegistrationList = () => {
  const [inscricoes, setInscricoes] = useState<RegistrationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<RegistrationListResponse['stats'] | null>(null);
  const [filters, setFilters] = useState<RegistrationFilters>({});

  const loadInscricoes = useCallback(async (page = 1, reset = false, newFilters?: RegistrationFilters) => {
    if (page === 1 || reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    setError(null);

    try {
      const filtersToUse = newFilters !== undefined ? newFilters : filters;
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(filtersToUse.store && { store: filtersToUse.store }),
        ...(filtersToUse.userName && { userName: filtersToUse.userName }),
        ...(filtersToUse.userDocument && { userDocument: filtersToUse.userDocument }),
        ...(filtersToUse.startDate && { startDate: filtersToUse.startDate }),
        ...(filtersToUse.endDate && { endDate: filtersToUse.endDate })
      });

      const response = await fetch(`${API_URL}registration/list?${params}`);
      
      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data: RegistrationListResponse = await response.json();

      // Mapear dados e calcular estatísticas localmente
      const mappedData = data.data?.map(item => ({
        ...item,
        id: item.registrationUid || item.id
      })) || [];

      if (reset || page === 1) {
        // Primeira carga ou filtro aplicado
        setInscricoes(mappedData);
      } else {
        // Carregar mais - concatena com dados existentes
        setInscricoes(prev => [...prev, ...mappedData]);
      }

      setTotal(data.total || 0);
      setCurrentPage(page);
      setHasMore(data.hasMore !== false && page < (data.totalPages || 1));
      
      // Calcular estatísticas com base em todos os dados carregados até agora
      if (reset || page === 1) {
        const localStats = data.stats || {
          totalComCheckin: mappedData.filter(item => item.temCheckin).length,
          totalSemCheckin: mappedData.filter(item => !item.temCheckin).length,
          porLoja: []
        };
        setStats(localStats);
      } else {
        // Para carregamento adicional, usar stats da API se disponível
        // ou manter as estatísticas anteriores (as estatísticas são globais, não incrementais)
        if (data.stats) {
          setStats(data.stats);
        }
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar inscrições';
      setError(errorMessage);
      console.error('Erro ao carregar inscrições:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters]);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadInscricoes(currentPage + 1, false);
    }
  }, [loadInscricoes, currentPage, loadingMore, hasMore]);

  const handleFilter = useCallback((newFilters: RegistrationFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    loadInscricoes(1, true, newFilters);
  }, [loadInscricoes]);

  const handleClearFilters = useCallback(() => {
    const emptyFilters = {};
    setFilters(emptyFilters);
    setCurrentPage(1);
    loadInscricoes(1, true, emptyFilters);
  }, [loadInscricoes]);

  const refresh = useCallback(() => {
    setCurrentPage(1);
    loadInscricoes(1, true);
  }, [loadInscricoes]);

  const copyAccessCode = useCallback(async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      return true;
    } catch (err) {
      console.error('Erro ao copiar código:', err);
      return false;
    }
  }, []);

  // Carregamento inicial
  useEffect(() => {
    loadInscricoes(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    inscricoes,
    loading,
    loadingMore,
    error,
    currentPage,
    hasMore,
    total,
    stats,
    filters,
    loadInscricoes,
    handleLoadMore,
    handleFilter,
    handleClearFilters,
    refresh,
    copyAccessCode
  };
}; 